'use client';

import { CheckIcon } from 'lucide-react';
import PhoneInputCore, {
	Country,
	FlagProps,
	Props as PhoneProps,
	Value as PhoneValue,
	getCountryCallingCode,
} from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command';
import { Input, InputProps } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { cn } from '@/utils/styles';

import { ScrollArea } from './scroll-area';

type PhoneInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> &
	Omit<PhoneProps<typeof PhoneInputCore>, 'onChange'> & {
		onChange?: (value: PhoneValue | undefined) => void;
	};

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
	React.ElementRef<typeof PhoneInputCore>,
	PhoneInputProps
>(({ className, onChange, ...props }, ref) => {
	return (
		<PhoneInputCore
			ref={ref}
			className={cn('flex', className)}
			flagComponent={FlagComponent}
			countrySelectComponent={CountrySelect}
			inputComponent={InputComponent}
			smartCaret={false}
			/**
			 * Handles the onChange event.
			 *
			 * react-phone-number-input might trigger the onChange event as undefined
			 * when a valid phone number is not entered. To prevent this,
			 * the value is coerced to an empty string.
			 *
			 * @param {PhoneValue | undefined} value - The entered value
			 */
			onChange={(value) => onChange?.(value)}
			{...props}
		/>
	);
});
PhoneInput.displayName = 'PhoneInput';

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, ...props }, ref) => (
		<Input
			className={cn(
				'h-[44px] rounded-e-md rounded-s-none border-slate-200 disabled:cursor-not-allowed disabled:opacity-100',
				className
			)}
			{...props}
			ref={ref}
		/>
	)
);
InputComponent.displayName = 'InputComponent';

type CountrySelectOption = { label: string; value: Country };

type CountrySelectProps = {
	disabled?: boolean;
	value: Country;
	onChange: (value: Country) => void;
	options: CountrySelectOption[];
};

const CountrySelect = ({ disabled, value, onChange, options }: CountrySelectProps) => {
	const handleSelect = React.useCallback(
		(country: Country) => {
			onChange(country);
		},
		[onChange]
	);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant={'outline'}
					className={cn(
						'flex h-[44px] gap-1 rounded-e-none rounded-s-md border-r-0 border-slate-200 px-3 disabled:cursor-not-allowed disabled:opacity-100'
					)}
					disabled={disabled}
				>
					<FlagComponent
						country={value}
						countryName={value}
					/>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[300px] border-slate-200 p-0">
				<Command>
					<CommandInput placeholder="Choose your country" />
					<CommandList>
						<ScrollArea className="h-72">
							<CommandEmpty>No country found.</CommandEmpty>
							<CommandGroup>
								{options
									.filter((x) => x.value)
									.map((option) => (
										<CommandItem
											className="gap-2"
											key={option.value}
											onSelect={() => handleSelect(option.value)}
										>
											<FlagComponent
												country={option.value}
												countryName={option.label}
											/>
											<span className="flex-1 text-sm">{option.label}</span>
											{option.value && (
												<span className="text-sm text-foreground/50">
													{`+${getCountryCallingCode(option.value)}`}
												</span>
											)}
											<CheckIcon
												className={cn(
													'ml-auto h-4 w-4',
													option.value === value ? 'opacity-100' : 'opacity-0'
												)}
											/>
										</CommandItem>
									))}
							</CommandGroup>
						</ScrollArea>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};

const FlagComponent = ({ country, countryName }: FlagProps) => {
	const Flag = flags[country];

	return (
		<span className="flex h-4 w-6 overflow-hidden rounded-sm bg-white">
			{Flag && <Flag title={countryName} />}
		</span>
	);
};
FlagComponent.displayName = 'FlagComponent';

export { PhoneInput };
